import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useUsage } from '@/contexts/UsageContext';
import Pressable from '@/components/base/Pressable';
import { useNavigate } from 'react-router-dom';

interface PremiumGateProps {
  feature: 'video_generate' | 'image_upload' | 'downloads' | 'hd_export';
  open: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
}

const featureConfig = {
  video_generate: {
    icon: 'ri-video-line',
    title: 'Video Limit Reached',
    description: 'Your verified account has reached its video allowance for this period. View the monthly plans for details.',
    freeLabel: '4 AI teaching videos/month',
    premiumLabel: '12 AI teaching videos/month',
  },
  image_upload: {
    icon: 'ri-image-line',
    title: 'Upload Limit Reached',
    description: 'Your daily image-upload limit has been reached. Try again after the daily reset. Checkout is not available yet.',
    freeLabel: '5 uploads/day',
    premiumLabel: '50 uploads/day',
  },
  downloads: {
    icon: 'ri-download-line',
    title: 'Downloads Locked',
    description: 'This download is not available. A Premium purchase cannot unlock downloads while checkout is unavailable.',
    freeLabel: 'Streaming only',
    premiumLabel: 'No additional download offer',
  },
  hd_export: {
    icon: 'ri-hd-line',
    title: 'HD Export Locked',
    description: 'HD export is not an available Premium benefit. View your lesson using the existing player.',
    freeLabel: 'Standard quality',
    premiumLabel: 'Same teaching visuals',
  },
};

export default function PremiumGate({ feature, open, onClose, onUpgrade }: PremiumGateProps) {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const config = featureConfig[feature];

  const t = {
    overlay: isDark ? 'bg-[#0F0F10]/70' : 'bg-black/40',
    panel: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    border: isDark ? 'border-white/[0.05]' : 'border-neutral-100',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    iconBg: isDark ? 'bg-white/[0.05]' : 'bg-neutral-100',
    checkIcon: 'text-emerald-500',
    freeRow: isDark ? 'bg-white/[0.02]' : 'bg-neutral-50',
    premiumRow: isDark ? 'bg-white/[0.04] border border-white/[0.08]' : 'bg-neutral-50 border border-neutral-200',
  };

  const [showing, setShowing] = useState(open);

  const handleClose = () => {
    setShowing(false);
    setTimeout(onClose, 300);
  };

  return (
    <AnimatePresence>
      {showing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`fixed inset-0 z-[100] ${t.overlay} backdrop-blur-md flex items-end sm:items-center justify-center`}
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 32 }}
            className={`w-full sm:max-w-[380px] sm:rounded-2xl rounded-t-2xl ${t.panel} border ${t.border} overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-3 text-center">
              <div className={`w-14 h-14 rounded-2xl ${t.iconBg} flex items-center justify-center mx-auto mb-3`}>
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${config.icon} ${isDark ? 'text-white' : 'text-neutral-900'} text-2xl`} />
                </div>
              </div>
              <h3 className={`text-base font-bold ${t.textPrimary}`}>{config.title}</h3>
              <p className={`text-xs ${t.textSecondary} mt-1 leading-relaxed max-w-[280px] mx-auto`}>
                {config.description}
              </p>
            </div>

            {/* Comparison */}
            <div className="px-5 pb-4 space-y-2">
              <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${t.freeRow}`}>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-line text-red-400 text-sm" />
                </div>
                <span className={`text-xs ${t.textMuted}`}>Free — {config.freeLabel}</span>
              </div>
              <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${t.premiumRow}`}>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-check-line ${t.checkIcon} text-sm`} />
                </div>
                <span className={`text-xs font-medium ${t.textPrimary}`}>Premium — {config.premiumLabel}</span>
              </div>
            </div>

            {/* CTA */}
            <div className="px-5 pb-5 space-y-2.5">
              <Pressable
                onClick={() => { handleClose(); navigate('/profile/premium'); }}
                className="w-full bg-neutral-900 text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                scale={0.97}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-sparkling-line text-sm" />
                </div>
                Upgrade to Premium
              </Pressable>
              <button
                onClick={handleClose}
                className={`w-full py-2.5 rounded-xl text-xs font-medium ${t.textMuted} cursor-pointer hover:opacity-80 transition-opacity`}
              >
                Maybe Later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}